// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Invoice Reconciliation Engine
// Tampa three-party audit: Contractor invoice vs. Monitor tickets vs. City review
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Divider,
  Alert,
} from '../../components/gab/GabComponents';
import { fetchTable, TABLES, FIELDS } from '../../lib/api-switch';
import { logActivity } from '../../lib/auditLog';

import {
  IconSearch, IconPlus, IconTrash, IconRefresh,
  IconScale, IconCheck, IconAlertTriangle,
  IconChevronLeft, IconUpload,
} from '@tabler/icons-react';
import * as XLSX from 'xlsx';
import { notifications } from '../../lib/notifications';
import { useTampaDebrisStore } from '../../store/tampaDebrisStore';
import { useClinDescriptions } from '../../hooks/useClinDescriptions';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { DEBRIS_TYPES, DEBRIS_TYPE_LABELS } from '../../types/debris';
import type {
  ReconciliationRecord, ReconciliationLineItem, ReconciliationException,
  ReconciliationStatus, DebrisType,
} from '../../types/debris';

// ── Status display config ─────────────────────────────────────
const STATUS_COLORS: Record<ReconciliationStatus, string> = {
  draft: '#939598',
  in_review: '#3b82f6',
  approved: 'var(--color-success)',
  disputed: 'var(--color-error)',
  resolved: '#03AC9E',
};

const STATUS_LABELS: Record<ReconciliationStatus, string> = {
  draft: 'Draft',
  in_review: 'In Review',
  approved: 'Approved',
  disputed: 'Disputed',
  resolved: 'Resolved',
};

const STATUS_OPTIONS = Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }));

const fmtMoney = (v: number) =>
  v > 0 ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';

const fmtPct = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;

// ── Payment status helper ─────────────────────────────────────
const PAYMENTS_BLOCK_RE = /\|\|\|PAYMENTS\|\|\|([\s\S]*?)\|\|\|ENDPAYMENTS\|\|\|/;

function getPaymentStatus(invoice: any): 'paid' | 'partial' | 'unpaid' {
  if (!invoice) return 'unpaid';
  const notes = invoice.reviewerNotes || invoice.notes || '';
  const m = notes.match(PAYMENTS_BLOCK_RE);
  let payments: any[] = [];
  if (m) { try { payments = JSON.parse(m[1]); } catch { /* */ } }
  if (!payments.length) return 'unpaid';
  const totalPaid = payments.reduce((s: number, p: any) => s + (p.amount || 0), 0);
  const invoiced = parseFloat(invoice.totalAmount || '0');
  if (invoiced > 0 && totalPaid >= invoiced * 0.99) return 'paid';
  if (totalPaid > 0) return 'partial';
  return 'unpaid';
}

const PAYMENT_COLORS = {
  paid:    { bg: '#dcfce7', color: '#166534', border: '#bbf7d0', label: 'PAID' },
  partial: { bg: '#fef3c7', color: '#92400e', border: '#fde68a', label: 'PARTIAL' },
  unpaid:  { bg: '#fee2e2', color: '#991b1b', border: '#fecaca', label: 'UNPAID' },
};

const fmtDate = (d: string) => {
  if (!d) return '-';
  const s = d.slice(0, 10);
  if (s.includes('-')) {
    const [y, m, day] = s.split('-');
    return `${m}/${day}/${y}`;
  }
  return s;
};

// ── CLIN codes from TDMS column map ───────────────────────────
// clinCode is stored in ticket notes meta or in the TDMS import data
// We group by debris type when clinCode is not available

export default function Reconciliation() {
  const navigate = useNavigate();
  const { activeIncident, user } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    reconciliations, loadingReconciliations,
    loadReconciliations, createReconciliation, updateReconciliation, deleteReconciliation,
  } = useTampaDebrisStore();

  const { loadTickets: allTickets, loadLoadTickets, invoices, loadInvoices } = useDebrisOpsStore();
  const { clinLabel } = useClinDescriptions();
  const { contractors, trucks, loadContractors, loadTrucks } = useDebrisStore();

  // ── UI state ─────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // By-contractor summary table sort
  const [ctrSortField, setCtrSortField] = useState('');
  const [ctrSortDir, setCtrSortDir] = useState<'asc' | 'desc'>('asc');

  // CLIN breakdown detail table sort
  const [clinSortField, setClinSortField] = useState('');
  const [clinSortDir, setClinSortDir] = useState<'asc' | 'desc'>('asc');

  // Exceptions detail table sort
  const [exSortField, setExSortField] = useState('');
  const [exSortDir, setExSortDir] = useState<'asc' | 'desc'>('asc');

  // Detail view — read from URL params via React Router
  const [searchParams, setSearchParams] = useSearchParams();
  const viewId = searchParams.get('view');
  const selectedRecon = viewId ? reconciliations.find(r => String(r.id) === viewId) || null : null;
  const setSelectedRecon = (r: ReconciliationRecord | null) => {
    if (r) setSearchParams({ view: String(r.id) });
    else setSearchParams({});
  };

  const renderDetailView = !!viewId;

  // New reconciliation wizard
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizContractorId, setWizContractorId] = useState<string | null>(null);
  const [wizPeriodStart, setWizPeriodStart] = useState('');
  const [wizPeriodEnd, setWizPeriodEnd] = useState('');
  const [wizInvoiceId, setWizInvoiceId] = useState<string | null>(null);
  // Manual entries: { clinCode: { qty, rate, amount } }
  const [wizManualEntries, setWizManualEntries] = useState<Record<string, { qty: number; rate: number; amount: number }>>({});
  const [wizLineItems, setWizLineItems] = useState<ReconciliationLineItem[]>([]);
  const [wizExceptions, setWizExceptions] = useState<ReconciliationException[]>([]);
  const [wizTotalInvoiced, setWizTotalInvoiced] = useState(0);
  const [wizTotalVerified, setWizTotalVerified] = useState(0);
  const [wizCalculated, setWizCalculated] = useState(false);
  const [wizNotes, setWizNotes] = useState('');
  const [wizServiceInvoice, setWizServiceInvoice] = useState(false);
  const [saving, setSaving] = useState(false);

  // Source-ticket drill-down
  const [drillDownLineItem, setDrillDownLineItem] = useState<ReconciliationLineItem | null>(null);

  // Re-open confirm
  const [reopenTarget, setReopenTarget] = useState<ReconciliationRecord | null>(null);

  // Approve confirm (when approved amount differs >5% from existing invoice)
  const [approveConfirm, setApproveConfirm] = useState<{
    recon: ReconciliationRecord;
    existingTotal: number;
    newTotal: number;
  } | null>(null);

  // Transaction detail documents from Evidence Archive
  const [txDocs, setTxDocs] = useState<{ id: string; title: string; notes: string; contractorId: string }[]>([]);
  const [txDocsLoading, setTxDocsLoading] = useState(false);
  const [selectedTxDocId, setSelectedTxDocId] = useState<string | null>(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<ReconciliationRecord | null>(null);

  // ── Load data on mount ──────────────────────────────────────
  useEffect(() => {
    loadReconciliations();
    loadLoadTickets();
    loadInvoices();
    loadContractors();
    loadTrucks();
  }, [loadReconciliations, loadLoadTickets, loadInvoices, loadContractors, loadTrucks]);

  // ── Helpers ──────────────────────────────────────────────────
  const contractorName = useCallback(
    (id: string) => contractors.find(c => String(c.id) === id)?.companyName ?? (id || '-'),
    [contractors],
  );

  const reconciledInvoiceIds = useMemo(() =>
    new Set(reconciliations.map(r => String(r.invoiceId)).filter(Boolean)),
    [reconciliations],
  );

  const invoiceSelectAll = useMemo(() =>
    invoices
      .filter(i => i.status !== 'consolidated')
      .sort((a, b) => (b.id || 0) - (a.id || 0))
      .map(i => {
        const amt = parseFloat(i.totalAmount || '0');
        const cName = contractorName(i.contractorId) || i.contractorId || '—';
        const alreadyRecon = reconciledInvoiceIds.has(String(i.id));
        return {
          value: String(i.id),
          label: `${i.invoiceNumber || '#' + i.id} — ${cName} · ${fmtDate(i.periodStart)}${i.periodEnd ? '–' + fmtDate(i.periodEnd) : ''}${amt > 0 ? ' · $' + amt.toLocaleString(undefined, { maximumFractionDigits: 0 }) : ''}${alreadyRecon ? ' (reconciled)' : ''}`,
        };
      }),
    [invoices, contractorName, reconciledInvoiceIds],
  );

  // CY calculation with fallback to load% x truck capacity
  const getTicketCY = useCallback((t: { calculatedCubicYards: string; loadPercentage: string; truckId: string }) => {
    const stored = parseFloat(t.calculatedCubicYards);
    if (stored > 0) return stored;
    const loadPct = parseFloat(t.loadPercentage) || 0;
    const truck = trucks.find(tr => String(tr.id) === t.truckId);
    const cap = truck ? parseFloat(truck.cubicYardCapacity || '0') : 0;
    return cap > 0 ? Math.round(cap * (loadPct / 100)) : 0;
  }, [trucks]);

  // ── Sorting ──────────────────────────────────────────────────
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const toggleCtrSort = (field: string) => {
    if (ctrSortField === field) setCtrSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setCtrSortField(field); setCtrSortDir('asc'); }
  };

  const toggleClinSort = (field: string) => {
    if (clinSortField === field) setClinSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setClinSortField(field); setClinSortDir('asc'); }
  };

  const toggleExSort = (field: string) => {
    if (exSortField === field) setExSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setExSortField(field); setExSortDir('asc'); }
  };

  // ── Filtered + sorted reconciliation list ────────────────────
  const filtered = useMemo(() => {
    let list = reconciliations;
    if (statusFilter && statusFilter !== 'all') list = list.filter(r => r.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        contractorName(r.contractorId).toLowerCase().includes(q) ||
        r.periodStart.includes(q) || r.periodEnd.includes(q),
      );
    }
    if (sortField) {
      return [...list].sort((a, b) => {
        const va = (a as any)[sortField] ?? '';
        const vb = (b as any)[sortField] ?? '';
        const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
        return sortDir === 'asc' ? n : -n;
      });
    }
    return [...list].sort((a, b) => b.id - a.id);
  }, [reconciliations, statusFilter, search, sortField, sortDir, contractorName]);

  // ── Reconciliation Engine ────────────────────────────────────
  const [reconLoading, setReconLoading] = useState(false);

  const runReconciliation = useCallback(async () => {
    if (!wizContractorId) {
      notifications.show({ message: 'Select a contractor first', color: '#C68700' });
      return;
    }
    if (!wizPeriodStart || !wizPeriodEnd) {
      notifications.show({ message: 'Set the invoice period dates', color: '#C68700' });
      return;
    }

    // ── Service invoice fast-path: no tickets expected ────────────
    if (wizServiceInvoice) {
      const lineItems: ReconciliationLineItem[] = Object.entries(wizManualEntries).map(([clinCode, entry]) => ({
        clinCode,
        description: DEBRIS_TYPE_LABELS[clinCode as DebrisType] ?? clinCode,
        invoicedQty: entry.qty,
        verifiedQty: entry.qty,
        variance: 0,
        variancePercent: 0,
        invoicedAmount: entry.amount,
        verifiedAmount: entry.amount,
        ticketNumbers: [],
        ticketIds: [],
        ticketCount: 0,
      }));
      const totalAmt = lineItems.reduce((s, li) => s + li.invoicedAmount, 0);
      setWizLineItems(lineItems);
      setWizExceptions([]);
      setWizTotalInvoiced(totalAmt);
      setWizTotalVerified(totalAmt);
      setWizCalculated(true);
      notifications.show({ message: `Service invoice — ${lineItems.length} line item(s), ${fmtMoney(totalAmt)} verified`, color: 'var(--color-success)' });
      return;
    }

    // 1. Fetch ALL tickets for this contractor directly from API (not the paginated store)
    setReconLoading(true);
    let matched: any[] = [];
    try {
      const { queryRecords: qr } = await import('../../api/client');
      const { TABLES: T2 } = await import('../../api/tables');
      const { FIELDS: F2 } = await import('../../api/fields');
      const FLT = F2.debrisLoadTickets;

      // Paginate through all tickets for this contractor
      const PAGE = 500;
      let offset = 0;
      let total = Infinity;
      const allRows: Record<string, unknown>[] = [];

      const filters: { field: string; operator: 'eq'; value: string }[] = [];
      if (incidentId) filters.push({ field: FLT.incidentId, operator: 'eq' as const, value: incidentId });

      while (offset < total) {
        const res = await qr<Record<string, unknown>>(T2.debrisLoadTickets, {
          limit: PAGE,
          offset,
          ...(filters.length ? { filters } : {}),
        });
        total = res.total;
        allRows.push(...res.records);
        offset += PAGE;
      }

      matched = allRows.map(row => {
        return {
          id: String(row.record_id ?? ''),
          ticketNumber: String(row[FLT.ticketNumber] ?? ''),
          contractorId: String(row[FLT.contractorId] ?? ''),
          debrisType: String(row[FLT.debrisType] ?? ''),
          calculatedCubicYards: String(row[FLT.calculatedCubicYards] ?? ''),
          measurementMode: String(row[FLT.measurementMode] ?? '') || 'volumetric',
          unitCount: String(row[FLT.unitCount] ?? ''),
          weight: String(row[FLT.weight] ?? ''),
          status: String(row[FLT.status] ?? ''),
          truckId: String(row[FLT.truckId] ?? ''),
          clinCode:          String(row[FLT.clinCode] ?? ''),
          extendedCost:      String(row[FLT.extendedCost] ?? ''),
          transactionRate:   String(row[FLT.transactionRate] ?? ''),
          subcontractorName: String(row[FLT.subcontractorName] ?? ''),
          loadDate:          String(row[FLT.loadDate] ?? ''),
        };
      }).filter(t => {
        if (t.status === 'voided') return false;
        // '0' is GAB's null sentinel — treat as unassigned, same as ''
        const hasContractor = t.contractorId && t.contractorId !== '0';
        if (hasContractor && t.contractorId !== wizContractorId) return false;
        return true;
      });

      const sampleClinCodes = [...new Set(matched.slice(0,20).map((t:any)=>t.clinCode||'[empty]'))];
      const invoiceKeys = Object.keys(wizManualEntries);
      // Log raw rows BEFORE mapping so we can see the actual field value
      const { FIELDS: F2raw } = await import('../../api/fields');
      const FLTraw = F2raw.debrisLoadTickets;
      const rawSample = allRows.slice(0,5).map(r => ({
        record_id: r.record_id,
        rawContractorId: r[FLTraw.contractorId],
        rawContractorIdType: typeof r[FLTraw.contractorId],
        mappedContractorId: String(r[FLTraw.contractorId] ?? ''),
      }));
      const sampleDebrisTypes = [...new Set(matched.slice(0,30).map((t:any) => t.debrisType || '[empty]'))];
    } catch (err) {
      console.error('[Reconciliation] fetch error', err);
      notifications.show({ message: 'Failed to load tickets', color: 'var(--color-error)' });
      setReconLoading(false);
      return;
    }
    setReconLoading(false);

    // 2. Group tickets by CLIN code (from meta), falling back to debris type
    const groups: Record<string, { qty: number; amount: number; rate: number; count: number; ticketNumbers: string[]; ticketIds: string[] }> = {};
    const seenTickets = new Set<string>();
    const duplicateExceptions: ReconciliationException[] = [];

    matched.forEach(t => {
      // Use actual CLIN code from TDMS import if available, else fall back to debris type
      const clinCode = t.clinCode || t.debrisType || 'unknown';
      const cfg = DEBRIS_TYPES[t.debrisType as DebrisType];
      const mode = t.measurementMode || cfg?.measurementMode || 'volumetric';

      // Check for duplicates
      if (seenTickets.has(t.ticketNumber)) {
        duplicateExceptions.push({
          ticketNumber: t.ticketNumber,
          reason: 'duplicate',
          monitorValue: `Ticket appears multiple times in verified data`,
        });
      }
      seenTickets.add(t.ticketNumber);

      if (!groups[clinCode]) groups[clinCode] = { qty: 0, amount: 0, rate: 0, count: 0, ticketNumbers: [], ticketIds: [] };

      let qty = 0;
      if (mode === 'volumetric') {
        const stored = parseFloat(t.calculatedCubicYards);
        qty = stored > 0 ? stored : 0;
      }
      else if (mode === 'unit') qty = parseFloat(t.unitCount || '0');
      else if (mode === 'weight') qty = parseFloat(t.weight || '0');

      const extCost = parseFloat(t.extendedCost || '0');
      const txRate = parseFloat(t.transactionRate || '0');

      groups[clinCode].qty += qty;
      groups[clinCode].amount += extCost;
      if (txRate > 0) groups[clinCode].rate = txRate; // last rate wins (they're the same per CLIN)
      groups[clinCode].count++;
      groups[clinCode].ticketNumbers.push(t.ticketNumber);
      groups[clinCode].ticketIds.push(t.id);
    });

    // 3. Build line items comparing invoiced vs verified (both qty and $)
    const lineItems: ReconciliationLineItem[] = [];
    const exceptions: ReconciliationException[] = [...duplicateExceptions];
    const allClinCodes = new Set([...Object.keys(groups), ...Object.keys(wizManualEntries)]);

    allClinCodes.forEach(clinCode => {
      const g = groups[clinCode];
      const verifiedQty = g?.qty ?? 0;
      const verifiedAmount = g?.amount ?? 0;
      const entry = wizManualEntries[clinCode];
      const invoicedQty = entry?.qty ?? 0;
      const invoicedAmount = entry?.amount ?? 0;

      const qtyVariance = invoicedQty - verifiedQty;
      const qtyVarPct = invoicedQty > 0 ? (qtyVariance / invoicedQty) * 100 : (verifiedQty > 0 ? -100 : 0);
      const amtVariance = invoicedAmount - verifiedAmount;

      lineItems.push({
        clinCode,
        description: DEBRIS_TYPE_LABELS[clinCode as DebrisType] ?? clinCode,
        invoicedQty,
        verifiedQty: Math.round(verifiedQty * 10) / 10,
        variance: Math.round(qtyVariance * 10) / 10,
        variancePercent: Math.round(qtyVarPct * 10) / 10,
        invoicedAmount,
        verifiedAmount: Math.round(verifiedAmount * 100) / 100,
        ticketNumbers: g?.ticketNumbers ?? [],
        ticketIds: g?.ticketIds ?? [],
        ticketCount: g?.count ?? 0,
      });

      // Flag exceptions
      if (invoicedQty > 0 && verifiedQty === 0) {
        exceptions.push({
          ticketNumber: clinCode,
          reason: 'phantom',
          contractorValue: `${invoicedQty.toLocaleString()} qty / ${fmtMoney(invoicedAmount)}`,
          monitorValue: 'No matching tickets found',
        });
      } else if (Math.abs(qtyVarPct) > 2 && invoicedQty > 0) {
        exceptions.push({
          ticketNumber: clinCode,
          reason: 'quantity',
          contractorValue: `${invoicedQty.toLocaleString()} / ${fmtMoney(invoicedAmount)}`,
          monitorValue: `${Math.round(verifiedQty).toLocaleString()} / ${fmtMoney(verifiedAmount)}`,
          notes: `Qty variance: ${fmtPct(qtyVarPct)}, $ variance: ${fmtMoney(Math.abs(amtVariance))}`,
        });
      }
    });

    // 4. Calculate totals (both qty and $)
    const totalInv = lineItems.reduce((s, li) => s + li.invoicedAmount, 0) || lineItems.reduce((s, li) => s + li.invoicedQty, 0);
    const totalVer = lineItems.reduce((s, li) => s + li.verifiedAmount, 0) || lineItems.reduce((s, li) => s + li.verifiedQty, 0);

    setWizLineItems(lineItems.sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance)));
    setWizExceptions(exceptions);
    setWizTotalInvoiced(totalInv);
    setWizTotalVerified(totalVer);
    setWizCalculated(true);

    notifications.show({
      message: `Reconciled: ${matched.length} tickets, ${lineItems.length} CLIN groups, ${exceptions.length} exceptions`,
      color: exceptions.length > 0 ? '#C68700' : 'var(--color-success)',
    });
  }, [wizContractorId, wizPeriodStart, wizPeriodEnd, allTickets, wizManualEntries, getTicketCY, wizServiceInvoice]);

  // ── Save reconciliation ──────────────────────────────────────
  const handleSaveReconciliation = async () => {
    if (!wizContractorId || !wizPeriodStart || !wizPeriodEnd) return;
    setSaving(true);
    try {
      const totalVariance = wizTotalInvoiced - wizTotalVerified;
      const variancePct = wizTotalInvoiced > 0 ? (totalVariance / wizTotalInvoiced) * 100 : 0;
      const notesPayload = [wizServiceInvoice ? '[Service invoice — no tickets expected]' : '', wizNotes].filter(Boolean).join('\n');
      await createReconciliation({
        incidentId,
        contractorId: wizContractorId,
        invoiceId: wizInvoiceId ?? '',
        periodStart: wizPeriodStart,
        periodEnd: wizPeriodEnd,
        status: 'draft',
        totalInvoiced: wizTotalInvoiced,
        totalVerified: wizTotalVerified,
        totalVariance,
        variancePercent: Math.round(variancePct * 10) / 10,
        lineItems: wizLineItems,
        exceptions: wizExceptions,
        reviewerNotes: notesPayload,
        approvedAmount: 0,
        approvedBy: '',
        approvedAt: '',
      });
      void logActivity('debris', 'reconciliation.created', 'reconciliation', wizContractorId, {
        contractor: contractorName(wizContractorId), period: `${wizPeriodStart}–${wizPeriodEnd}`,
        totalInvoiced: wizTotalInvoiced, totalVerified: wizTotalVerified,
      });
      notifications.show({ message: 'Reconciliation saved', color: 'var(--color-success)' });
      resetWizard();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const resetWizard = () => {
    setWizardOpen(false);
    setWizContractorId(null);
    setWizPeriodStart('');
    setWizPeriodEnd('');
    setWizInvoiceId(null);
    setWizManualEntries({});
    setWizLineItems([]);
    setWizExceptions([]);
    setWizTotalInvoiced(0);
    setWizTotalVerified(0);
    setWizCalculated(false);
    setWizNotes('');
    setWizServiceInvoice(false);
  };

  // ── Detail view actions ──────────────────────────────────────
  const handleApprove = async (recon: ReconciliationRecord) => {
    try {
      const approvedAmount = recon.totalVerified;
      await updateReconciliation(recon.id, {
        status: 'approved',
        approvedAmount,
        approvedBy: user?.email ?? '',
        approvedAt: new Date().toISOString(),
      });

      // ── Auto-create or update invoice (Feature 4) ──────────
      try {
        const { createInvoice, updateInvoice } = useDebrisOpsStore.getState();
        const existingInv = invoices.find(
          i => i.contractorId === recon.contractorId &&
               i.periodStart === recon.periodStart &&
               i.periodEnd === recon.periodEnd
        );
        const tampaMeta = JSON.stringify({
          reconciliationId: String(recon.id),
          monitorVerifiedAmount: recon.totalVerified,
          contractorSubmittedAmount: recon.totalInvoiced,
        });

        if (existingInv) {
          await updateInvoice(existingInv.id, {
            status: 'reconciled',
            totalAmount: String(approvedAmount),
            notes: `${(existingInv.notes || '').replace(/\|\|\|TAMPA\|\|\|[\s\S]*?\|\|\|ENDTAMPA\|\|\|/g, '').trim()}\n|||TAMPA|||${tampaMeta}|||ENDTAMPA|||`,
          });
          notifications.show({
            message: `Invoice ${existingInv.invoiceNumber} updated. Approved: ${fmtMoney(approvedAmount)}.`,
            color: 'var(--color-success)',
          });
        } else {
          const invNumber = recon.invoiceId ? `RECON-${recon.invoiceId}` : `RECON-${recon.id}`;
          await createInvoice({
            incidentId: incidentId || String(recon.incidentId || ''),
            contractorId: String(recon.contractorId || ''),
            invoiceNumber: invNumber,
            periodStart: (recon.periodStart || '').slice(0, 10),
            periodEnd: (recon.periodEnd || '').slice(0, 10),
            totalAmount: String(Math.round(approvedAmount * 100) / 100),
            status: 'reconciled',
            notes: `|||TAMPA|||${tampaMeta}|||ENDTAMPA|||`,
            lineItems: JSON.stringify(recon.lineItems || []),
          });
          notifications.show({
            message: `Invoice auto-created for ${fmtMoney(approvedAmount)}.`,
            color: 'var(--color-success)',
          });
        }
      } catch (invErr) {
        console.error('[Reconciliation] Auto-invoice failed:', invErr);
        notifications.show({ message: `Reconciliation approved, but auto-invoice creation failed: ${invErr instanceof Error ? invErr.message : String(invErr)}`, color: '#C68700' });
      }

      // Reload invoices to reflect the auto-created one
      try { useDebrisOpsStore.getState().loadInvoices(); } catch { /* ignore */ }

      void logActivity('debris', 'reconciliation.approved', 'reconciliation', recon.id, {
        contractor: contractorName(recon.contractorId), approvedAmount,
        period: `${recon.periodStart}–${recon.periodEnd}`,
      });
      setSelectedRecon(null);
      notifications.show({ message: 'Reconciliation approved', color: 'var(--color-success)' });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Approve failed', color: 'var(--color-error)' });
    }
  };

  const handleDispute = async (recon: ReconciliationRecord) => {
    try {
      await updateReconciliation(recon.id, { status: 'disputed' });
      void logActivity('debris', 'reconciliation.disputed', 'reconciliation', recon.id, {
        contractor: contractorName(recon.contractorId), period: `${recon.periodStart}–${recon.periodEnd}`,
      });
      setSelectedRecon(null);
      notifications.show({ message: 'Reconciliation flagged as disputed', color: '#C68700' });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Update failed', color: 'var(--color-error)' });
    }
  };

  const handleReopen = async (recon: ReconciliationRecord) => {
    try {
      await updateReconciliation(recon.id, {
        status: 'in_review',
        approvedAmount: 0,
        approvedBy: '',
        approvedAt: '',
      });
      void logActivity('debris', 'reconciliation.reopened', 'reconciliation', recon.id, {
        contractor: contractorName(recon.contractorId), previousStatus: recon.status,
      });
      setReopenTarget(null);
      setSelectedRecon(null);
      notifications.show({ message: 'Reconciliation re-opened for review', color: '#3b82f6' });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Re-open failed', color: 'var(--color-error)' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteReconciliation(deleteTarget.id);
      setDeleteTarget(null);
      notifications.show({ message: 'Reconciliation deleted', color: '#939598' });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  // ── Populate manual entries from selected invoice ────────────
  useEffect(() => {
    if (wizInvoiceId) {
      const inv = invoices.find(i => String(i.id) === wizInvoiceId);
      if (inv) {
        if (inv.periodStart && !wizPeriodStart) setWizPeriodStart(inv.periodStart.slice(0, 10));
        if (inv.periodEnd && !wizPeriodEnd) setWizPeriodEnd(inv.periodEnd.slice(0, 10));
        if (inv.contractorId && !wizContractorId) setWizContractorId(inv.contractorId);
        // Line items are already loaded by the picker's onChange — no duplicate load here
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizInvoiceId]);

  // ── Auto-load saved transaction detail data ──────────────────
  useEffect(() => {
    if (!wizContractorId || wizInvoiceId) return; // invoice takes priority
    if (Object.keys(wizManualEntries).length > 0) return; // already populated
    (async () => {
      try {
        const { queryRecords: qr } = await import('../../api/client');
        const { TABLES: T2 } = await import('../../api/tables');
        const { FIELDS: F2 } = await import('../../api/fields');
        const FD = F2.debrisDocuments;
        const TXDATA_RE = /\|\|\|TXDATA\|\|\|([\s\S]*?)\|\|\|ENDTXDATA\|\|\|/;
        const res = await qr<Record<string, unknown>>(T2.debrisDocuments, {
          limit: 100,
          filters: [{ field: FD.documentType, operator: 'eq' as const, value: 'transaction_detail' }],
        });
        // Find transaction details for this contractor
        for (const doc of res.records) {
          const notes = String(doc[FD.notes] || '');
          const mm = notes.match(TXDATA_RE);
          if (!mm) continue;
          try {
            const txData = JSON.parse(mm[1]);
            if (txData.contractorId && txData.contractorId !== wizContractorId) continue;
            // Found matching transaction data — populate entries
            const entries: Record<string, { qty: number; rate: number; amount: number }> = {};
            for (const [clin, g] of Object.entries(txData.clinGroups || {}) as any) {
              entries[clin] = { qty: Math.round(g.qty * 100) / 100, rate: Math.round(g.rate * 100) / 100, amount: Math.round(g.amount * 100) / 100 };
            }
            if (Object.keys(entries).length > 0) {
              setWizManualEntries(entries);
              if (txData.periodStart && !wizPeriodStart) setWizPeriodStart(txData.periodStart);
              if (txData.periodEnd && !wizPeriodEnd) setWizPeriodEnd(txData.periodEnd);
              notifications.show({ message: `Auto-loaded ${Object.keys(entries).length} CLINs from ${String(doc[FD.title] || 'transaction detail')}`, color: 'var(--color-success)' });
              break;
            }
          } catch { /* skip malformed */ }
        }
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizContractorId]);

  // ── Load rate schedules for CLIN options ─────────────────────
  const [rateSchedules, setRateSchedules] = useState<any[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const { fetchRecords: fetchRecs } = await import('../../api/client');
        const { TABLES: T2 } = await import('../../api/tables');
        const { FIELDS: F2 } = await import('../../api/fields');
        const res = await fetchRecs<Record<string, unknown>>(T2.debrisRateSchedules, { limit: 500 });
        const FR = F2.debrisRateSchedules;
        setRateSchedules(res.records.map((r: any) => ({
          id: r.record_id,
          contractorId: String(r[FR.contractorId] || ''),
          lineItemCode: String(r[FR.lineItemCode] || ''),
          description: String(r[FR.description] || ''),
          rateTier1: String(r[FR.rateTier1] || ''),
          rateTier2: String(r[FR.rateTier2] || ''),
        })));
      } catch { /* ignore */ }
    })();
  }, []);

  // ── CLIN options for manual entry ────────────────────────────
  const clinOptions = useMemo(() => {
    const opts: { value: string; label: string; rate?: number }[] = [];
    const seen = new Set<string>();
    // Add CLIN codes from rate schedules for the selected contractor
    rateSchedules
      .filter((rs: any) => !wizContractorId || String(rs.contractorId) === wizContractorId)
      .forEach((rs: any) => {
        const code = rs.lineItemCode || '';
        if (code && !seen.has(code)) {
          seen.add(code);
          opts.push({
            value: code,
            label: `${code} — ${rs.description || ''}`.slice(0, 60),
            rate: parseFloat(rs.rateTier1 || rs.rateTier2 || '0'),
          });
        }
      });
    // Also add debris type fallbacks for tickets without CLIN codes
    Object.entries(DEBRIS_TYPE_LABELS).forEach(([value, label]) => {
      if (!seen.has(value)) {
        seen.add(value);
        opts.push({ value, label });
      }
    });
    return opts;
  }, [rateSchedules, wizContractorId]);

  // ── Sort header helpers ─────────────────────────────────────
  const thStyle = (col: string): React.CSSProperties => ({
    padding: '8px 16px', textAlign: 'left', cursor: 'pointer', userSelect: 'none',
    color: sortField === col ? '#4B3FFF' : '#64748b', position: 'sticky', top: 0,
    zIndex: 1, background: '#f8fafc',
  });

  const sortIndicator = (col: string) =>
    sortField === col ? (sortDir === 'desc' ? ' \u2193' : ' \u2191') : <span style={{ opacity: 0.25 }}> &uarr;</span>;

  // By-contractor table header helpers
  const ctrThStyle = (col: string, align: 'left' | 'right' | 'center' = 'left'): React.CSSProperties => ({
    padding: '6px 12px', textAlign: align, cursor: 'pointer', userSelect: 'none',
    color: ctrSortField === col ? '#4B3FFF' : '#64748b', position: 'sticky', top: 0,
    zIndex: 1, background: '#f8fafc',
  });

  const ctrSortIndicator = (col: string) =>
    ctrSortField === col ? (ctrSortDir === 'desc' ? ' \u2193' : ' \u2191') : <span style={{ opacity: 0.25 }}> &uarr;</span>;

  // CLIN breakdown detail table header helpers
  const clinThStyle = (col: string, align: 'left' | 'right' = 'left'): React.CSSProperties => ({
    padding: '6px 12px', textAlign: align, cursor: 'pointer', userSelect: 'none',
    color: clinSortField === col ? '#4B3FFF' : '#64748b', position: 'sticky', top: 0,
    zIndex: 1, background: '#f8fafc',
  });

  const clinSortIndicator = (col: string) =>
    clinSortField === col ? (clinSortDir === 'desc' ? ' \u2193' : ' \u2191') : <span style={{ opacity: 0.25 }}> &uarr;</span>;

  // Exceptions detail table header helpers
  const exThStyle = (col: string, align: 'left' | 'right' = 'left'): React.CSSProperties => ({
    padding: '6px 12px', textAlign: align, cursor: 'pointer', userSelect: 'none',
    color: exSortField === col ? '#4B3FFF' : '#64748b', position: 'sticky', top: 0,
    zIndex: 1, background: '#f8fafc',
  });

  const exSortIndicator = (col: string) =>
    exSortField === col ? (exSortDir === 'desc' ? ' \u2193' : ' \u2191') : <span style={{ opacity: 0.25 }}> &uarr;</span>;

  // ── Dashboard metrics ──────────────────────────────────────
  const dashTotalContractSpend = useMemo(() =>
    contractors.reduce((s, c) => s + (parseFloat(c.contractAmount || '0') || 0), 0),
    [contractors],
  );

  const dashReconciled = useMemo(() => {
    try { return reconciliations.filter(r => r.status === 'approved').reduce((s, r) => s + (Number(r.approvedAmount) || 0), 0); } catch { return 0; }
  }, [reconciliations]);

  const dashPendingCount = useMemo(() => {
    try { return reconciliations.filter(r => r.status === 'draft' || r.status === 'in_review').length; } catch { return 0; }
  }, [reconciliations]);

  const dashActionNeeded = useMemo(() => {
    try {
      const reconContractorIds = new Set(reconciliations.map(r => r.contractorId));
      const unreconciledContractors = contractors.filter(c => !reconContractorIds.has(String(c.id))).length;
      return unreconciledContractors;
    } catch { return 0; }
  }, [contractors, reconciliations]);

  // By-contractor breakdown
  const contractorBreakdown = useMemo(() => {
    try {
    const result: { id: string; name: string; totalInvoiced: number; totalReconciled: number; status: string; qualityScored: number; qualityTotal: number }[] = [];
    contractors.forEach(c => {
      const cId = String(c.id);
      const cInvoices = invoices.filter(i => i.contractorId === cId);
      const cRecons = reconciliations.filter(r => r.contractorId === cId);
      const totalInv = cInvoices.reduce((s, i) => s + (parseFloat(i.totalAmount || '0') || 0), 0);
      const totalRec = cRecons.filter(r => r.status === 'approved').reduce((s, r) => s + (r.approvedAmount || 0), 0);
      const hasApproved = cRecons.some(r => r.status === 'approved');
      const hasPending = cRecons.some(r => r.status === 'draft' || r.status === 'in_review');
      const status = hasApproved ? 'reconciled' : hasPending ? 'pending' : 'none';
      // Quality gate counts
      const qualityTotal = cInvoices.length;
      const qualityScored = cInvoices.filter(inv => {
        const rawNotes = inv.notes ?? '';
        const TAMPA_RE = /\|\|\|TAMPA\|\|\|([\s\S]*?)\|\|\|ENDTAMPA\|\|\|/;
        const tm = rawNotes.match(TAMPA_RE);
        if (!tm) return false;
        try { const m = JSON.parse(tm[1]); return m.qualityChecks && Object.values(m.qualityChecks).some(Boolean); } catch { return false; }
      }).length;
      result.push({ id: cId, name: c.companyName || `#${c.id}`, totalInvoiced: totalInv, totalReconciled: totalRec, status, qualityScored, qualityTotal });
    });
    return result.sort((a, b) => b.totalInvoiced - a.totalInvoiced);
    } catch { return []; }
  }, [contractors, invoices, reconciliations]);

  // Sorted by-contractor breakdown (applies user sort)
  const sortedContractorBreakdown = useMemo(() => {
    if (!ctrSortField) return contractorBreakdown;
    return [...contractorBreakdown].sort((a, b) => {
      const va = (a as any)[ctrSortField] ?? '';
      const vb = (b as any)[ctrSortField] ?? '';
      const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return ctrSortDir === 'asc' ? n : -n;
    });
  }, [contractorBreakdown, ctrSortField, ctrSortDir]);

  // ═══════════════════════════════════════════════════════════
  // DETAIL VIEW (if ?view= is in URL) — AFTER all hooks
  // ═══════════════════════════════════════════════════════════
  if (renderDetailView) {
    if (!selectedRecon) {
      return (
        <Stack p="xl" align="center">
          <Text c="dimmed">Loading reconciliation #{String(viewId)}...</Text>
        </Stack>
      );
    }

    const safeStatus = String(selectedRecon.status || 'draft') as ReconciliationStatus;
    const detailLineItems = Array.isArray(selectedRecon.lineItems) ? selectedRecon.lineItems : [];
    const detailExceptions = Array.isArray(selectedRecon.exceptions) ? selectedRecon.exceptions : [];
    const totalInv = Number(selectedRecon.totalInvoiced) || 0;
    const totalVer = Number(selectedRecon.totalVerified) || 0;
    const totalVar = Math.abs(totalInv - totalVer);
    const exCount = detailExceptions.length;

    return (
      <Stack gap={12}>
        <Button variant="light" size="xs" leftSection={<IconChevronLeft size={14} />}
          onClick={() => setSelectedRecon(null)}
          style={{ alignSelf: 'flex-start' }}>
          Back to List
        </Button>

        <Group gap="sm" align="center">
          <Title order={3}>Reconciliation #{String(selectedRecon.id)}</Title>
          <Badge size="lg" color={STATUS_COLORS[safeStatus]}>{String(STATUS_LABELS[safeStatus] || safeStatus)}</Badge>
        </Group>

        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
          <Paper p="md" radius="md" withBorder style={{ textAlign: 'center', borderTop: '3px solid #3b82f6' }}>
            <Text size="xs" c="dimmed" mb={4}>Total Invoiced</Text>
            <Text size="xl" fw={800} c="#3b82f6">{String(fmtMoney(totalInv))}</Text>
          </Paper>
          <Paper p="md" radius="md" withBorder style={{ textAlign: 'center', borderTop: '3px solid var(--color-success)' }}>
            <Text size="xs" c="dimmed" mb={4}>Total Verified</Text>
            <Text size="xl" fw={800} c="var(--color-success)">{String(fmtMoney(totalVer))}</Text>
          </Paper>
          <Paper p="md" radius="md" withBorder style={{ textAlign: 'center', borderTop: '3px solid #C68700' }}>
            <Text size="xs" c="dimmed" mb={4}>Variance</Text>
            <Text size="xl" fw={800} c="#C68700">{String(fmtMoney(totalVar))}</Text>
          </Paper>
          <Paper p="md" radius="md" withBorder style={{ textAlign: 'center', borderTop: '3px solid #64748b' }}>
            <Text size="xs" c="dimmed" mb={4}>Exceptions</Text>
            <Text size="xl" fw={800} c={exCount > 0 ? 'var(--color-error)' : 'var(--color-success)'}>{String(exCount)}</Text>
          </Paper>
        </SimpleGrid>

        <Paper p="sm" radius="md" withBorder>
          <Group gap="xl" wrap="wrap">
            <Text size="sm"><Text span fw={600}>Contractor:</Text> {String(contractorName(String(selectedRecon.contractorId || '')))}</Text>
            <Text size="sm"><Text span fw={600}>Period:</Text> {String(fmtDate(String(selectedRecon.periodStart || '')))} — {String(fmtDate(String(selectedRecon.periodEnd || '')))}</Text>
            {(() => {
              const detailLinkedInvoice = invoices.find(i => String(i.id) === String(selectedRecon.invoiceId));
              const detailPayStatus = getPaymentStatus(detailLinkedInvoice);
              const detailPayRef = detailLinkedInvoice
                ? (() => { const m = (detailLinkedInvoice.reviewerNotes || '').match(/paymentRef['":\s]+([^"}\s,]+)/); return m?.[1] ?? ''; })()
                : '';
              return (
                <Group gap={6} align="center">
                  <Text size="sm" fw={600}>Payment:</Text>
                  <Badge size="sm" style={{
                    background: PAYMENT_COLORS[detailPayStatus].bg,
                    color: PAYMENT_COLORS[detailPayStatus].color,
                    border: `1px solid ${PAYMENT_COLORS[detailPayStatus].border}`,
                  }}>
                    {PAYMENT_COLORS[detailPayStatus].label}
                  </Badge>
                  {detailPayRef && (
                    <Text size="xs" ff="monospace" c="dimmed">#{detailPayRef}</Text>
                  )}
                </Group>
              );
            })()}
          </Group>
        </Paper>

        {(() => {
          const varPct = totalInv > 0 ? Math.abs((totalInv - totalVer) / totalInv) * 100 : 0;
          if (varPct < 5 && exCount === 0) return null;
          const isCritical = varPct > 10 || detailExceptions.some((e: any) => e.reason === 'phantom');
          return (
            <Paper p="sm" radius="md" withBorder style={{
              background: isCritical ? '#fef2f2' : '#fffbeb',
              borderColor: isCritical ? '#fca5a5' : '#fbbf24',
              borderLeft: `4px solid ${isCritical ? '#D33423' : '#C68700'}`,
            }}>
              <Group gap="xs" align="flex-start">
                <IconAlertTriangle size={16} color={isCritical ? '#D33423' : '#C68700'} style={{ marginTop: 2, flexShrink: 0 }} />
                <div>
                  <Text size="sm" fw={600} c={isCritical ? '#D33423' : '#C68700'}>
                    {isCritical ? 'Critical variance — requires resolution before approval' : 'Variance detected — review before approving'}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {varPct > 0 ? `${varPct.toFixed(1)}% overall variance (${fmtMoney(Math.abs(totalInv - totalVer))})` : ''}
                    {exCount > 0 ? `${varPct > 0 ? ' · ' : ''}${exCount} exception${exCount !== 1 ? 's' : ''} flagged` : ''}
                  </Text>
                </div>
              </Group>
            </Paper>
          );
        })()}

        {detailLineItems.length > 0 && (
          <Paper withBorder radius="md">
            <Text fw={700} size="sm" p="sm" pb={0}>CLIN Breakdown ({String(detailLineItems.length)} items)</Text>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 400 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                    <th style={clinThStyle('clinCode', 'left')} onClick={() => toggleClinSort('clinCode')}>CLIN{clinSortIndicator('clinCode')}</th>
                    <th style={clinThStyle('ticketCount', 'right')} onClick={() => toggleClinSort('ticketCount')}>Tickets{clinSortIndicator('ticketCount')}</th>
                    <th style={clinThStyle('invoicedQty', 'right')} onClick={() => toggleClinSort('invoicedQty')}>Invoiced Qty{clinSortIndicator('invoicedQty')}</th>
                    <th style={clinThStyle('verifiedQty', 'right')} onClick={() => toggleClinSort('verifiedQty')}>Verified Qty{clinSortIndicator('verifiedQty')}</th>
                    <th style={clinThStyle('variance', 'right')} onClick={() => toggleClinSort('variance')}>Variance{clinSortIndicator('variance')}</th>
                    <th style={clinThStyle('variancePercent', 'right')} onClick={() => toggleClinSort('variancePercent')}>Var %{clinSortIndicator('variancePercent')}</th>
                    <th style={clinThStyle('invoicedAmount', 'right')} onClick={() => toggleClinSort('invoicedAmount')}>Invoiced ${clinSortIndicator('invoicedAmount')}</th>
                    <th style={clinThStyle('verifiedAmount', 'right')} onClick={() => toggleClinSort('verifiedAmount')}>Verified ${clinSortIndicator('verifiedAmount')}</th>
                    <th style={{ padding: '6px 12px', position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {[...detailLineItems].sort((a: any, b: any) => {
                    if (!clinSortField) return 0;
                    const va = a[clinSortField] ?? '';
                    const vb = b[clinSortField] ?? '';
                    const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
                    return clinSortDir === 'asc' ? n : -n;
                  }).map((li: any, idx: number) => {
                    const isHigh = Math.abs(Number(li?.variancePercent) || 0) > 2;
                    const tickets: string[] = Array.isArray(li?.ticketNumbers) ? li.ticketNumbers : [];
                    return (
                      <tr key={idx} style={{
                        borderBottom: '1px solid #e2e8f0',
                        backgroundColor: isHigh ? (Math.abs(Number(li?.variancePercent) || 0) > 10 ? '#fef2f2' : '#fffbeb') : undefined,
                      }}>
                        <td style={{ padding: '6px 12px', fontWeight: 500 }}>{clinLabel(String(li?.clinCode || '')) !== '-' ? clinLabel(String(li?.clinCode || '')) : String(li?.description || '-')}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>
                          {tickets.length > 0 ? String(tickets.length) : String(li?.ticketCount || 0)}
                        </td>
                        <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{String(Number(li?.invoicedQty || 0).toLocaleString())}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{String(Number(li?.verifiedQty || 0).toLocaleString())}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace', color: isHigh ? '#D33423' : undefined }}>
                          {String(Number(li?.variance || 0) > 0 ? '+' : '')}{String(Number(li?.variance || 0).toLocaleString())}
                        </td>
                        <td style={{ padding: '6px 12px', textAlign: 'right', color: isHigh ? '#C68700' : '#16a34a' }}>
                          {String(fmtPct(Number(li?.variancePercent) || 0))}
                        </td>
                        <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{String(fmtMoney(Number(li?.invoicedAmount) || 0))}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{String(fmtMoney(Number(li?.verifiedAmount) || 0))}</td>
                        <td style={{ padding: '4px 8px' }}>
                          {tickets.length > 0 && (
                            <button
                              onClick={() => setDrillDownLineItem(li)}
                              style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #cbd5e1', background: '#f8fafc', cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              View {tickets.length} tickets
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Paper>
        )}

        {detailExceptions.length > 0 && (
          <Paper withBorder radius="md">
            <Text fw={700} size="sm" p="sm" pb={0} c="var(--color-error)">
              {String(detailExceptions.length)} Exception{detailExceptions.length !== 1 ? 's' : ''}
            </Text>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 300 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                    <th style={exThStyle('ticketNumber', 'left')} onClick={() => toggleExSort('ticketNumber')}>Ticket / CLIN{exSortIndicator('ticketNumber')}</th>
                    <th style={exThStyle('reason', 'left')} onClick={() => toggleExSort('reason')}>Reason{exSortIndicator('reason')}</th>
                    <th style={exThStyle('contractorValue', 'left')} onClick={() => toggleExSort('contractorValue')}>Contractor Value{exSortIndicator('contractorValue')}</th>
                    <th style={exThStyle('monitorValue', 'left')} onClick={() => toggleExSort('monitorValue')}>Monitor Value{exSortIndicator('monitorValue')}</th>
                    <th style={exThStyle('notes', 'left')} onClick={() => toggleExSort('notes')}>Notes{exSortIndicator('notes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...detailExceptions].sort((a: any, b: any) => {
                    if (!exSortField) return 0;
                    const va = a[exSortField] ?? '';
                    const vb = b[exSortField] ?? '';
                    const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
                    return exSortDir === 'asc' ? n : -n;
                  }).map((ex: ReconciliationException, idx: number) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '6px 12px', fontWeight: 500 }}>{String(ex.ticketNumber || '-')}</td>
                      <td style={{ padding: '6px 12px' }}>
                        <Badge size="xs" color={String(ex.reason) === 'phantom' ? 'var(--color-error)' : '#C68700'}>{String(ex.reason || '-')}</Badge>
                      </td>
                      <td style={{ padding: '6px 12px' }}>{String(ex.contractorValue || '-')}</td>
                      <td style={{ padding: '6px 12px' }}>{String(ex.monitorValue || '-')}</td>
                      <td style={{ padding: '6px 12px' }}>{String(ex.notes || '-')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Paper>
        )}

        {selectedRecon.reviewerNotes ? (
          <Paper p="sm" radius="md" withBorder>
            <div>
              <Text size="xs" c="dimmed">Reviewer Notes</Text>
              <Text size="sm">{String(selectedRecon.reviewerNotes)}</Text>
            </div>
          </Paper>
        ) : null}

        {/* If Approved preview — shown while pending */}
        {safeStatus !== 'approved' && safeStatus !== 'resolved' && selectedRecon.totalVerified > 0 && (
          <Paper p="sm" radius="md" withBorder style={{ borderLeft: '3px solid #3b82f6', background: '#f0f7ff' }}>
            <Text size="xs" c="dimmed" fw={600} mb={6} style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              If Approved
            </Text>
            <div>
              <Text size="xs" c="dimmed">Verified Total</Text>
              <Text size="sm" fw={700}>{fmtMoney(selectedRecon.totalVerified)}</Text>
            </div>
          </Paper>
        )}

        {safeStatus === 'approved' && (
          <Paper p="sm" radius="md" withBorder style={{ borderLeft: '3px solid var(--color-success)' }}>
            <Text size="sm">Approved by {String(selectedRecon.approvedBy || '-')} on {String(fmtDate(String(selectedRecon.approvedAt || '')))}</Text>
            <Text size="sm" fw={600} c="var(--color-success)">Approved Amount: {String(fmtMoney(Number(selectedRecon.approvedAmount) || 0))}</Text>
          </Paper>
        )}

        {canEdit && (
          <Group gap="sm">
            {safeStatus !== 'approved' && safeStatus !== 'resolved' && (
              <Button color="var(--color-success)" leftSection={<IconCheck size={14} />}
                onClick={() => {
                  const existingInv = invoices.find(
                    i => i.contractorId === selectedRecon.contractorId &&
                         i.periodStart === selectedRecon.periodStart &&
                         i.periodEnd === selectedRecon.periodEnd
                  );
                  const newTotal = selectedRecon.totalVerified;
                  if (existingInv) {
                    const existingTotal = parseFloat(existingInv.totalAmount) || 0;
                    if (existingTotal > 0 && Math.abs(newTotal - existingTotal) / existingTotal > 0.05) {
                      setApproveConfirm({ recon: selectedRecon, existingTotal, newTotal });
                      return;
                    }
                  }
                  void handleApprove(selectedRecon);
                }}>
                Approve
              </Button>
            )}
            {safeStatus !== 'approved' && safeStatus !== 'disputed' && safeStatus !== 'resolved' && (
              <Button variant="light" color="var(--color-error)" leftSection={<IconAlertTriangle size={14} />}
                onClick={() => handleDispute(selectedRecon)}>
                Dispute
              </Button>
            )}
            {(safeStatus === 'approved' || safeStatus === 'disputed') && (
              <Button variant="light" color="blue" leftSection={<IconChevronLeft size={14} />}
                onClick={() => setReopenTarget(selectedRecon)}>
                Re-open for Review
              </Button>
            )}
          </Group>
        )}
      </Stack>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // LIST VIEW
  // ═══════════════════════════════════════════════════════════
  return (
    <div>
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm" mb="md">
        <Paper p="md" radius="md" withBorder style={{ textAlign: 'center', borderTop: '3px solid #0284c7' }}>
          <Text size="xs" c="dimmed" mb={4}>Total Contractor Spend</Text>
          <Text size="xl" fw={800} c="#0284c7">{fmtMoney(dashTotalContractSpend)}</Text>
          <Text size="xs" c="dimmed">{String(contractors.length)} contractors</Text>
        </Paper>
        <Paper p="md" radius="md" withBorder style={{ textAlign: 'center', borderTop: '3px solid var(--color-success)' }}>
          <Text size="xs" c="dimmed" mb={4}>Reconciled</Text>
          <Text size="xl" fw={800} c="var(--color-success)">{fmtMoney(dashReconciled)}</Text>
          <Text size="xs" c="dimmed">{String(reconciliations.filter(r => r.status === 'approved').length)} approved</Text>
        </Paper>
        <Paper p="md" radius="md" withBorder style={{ textAlign: 'center', borderTop: '3px solid #3b82f6' }}>
          <Text size="xs" c="dimmed" mb={4}>Pending Review</Text>
          <Text size="xl" fw={800} c="#3b82f6">{String(dashPendingCount)}</Text>
          <Text size="xs" c="dimmed">draft / in review</Text>
        </Paper>
        <Paper p="md" radius="md" withBorder style={{ textAlign: 'center', borderTop: '3px solid #C68700' }}>
          <Text size="xs" c="dimmed" mb={4}>Action Needed</Text>
          <Text size="xl" fw={800} c="#C68700">{String(dashActionNeeded)}</Text>
          <Text size="xs" c="dimmed">unreconciled + unscored</Text>
        </Paper>
      </SimpleGrid>

      {contractorBreakdown.length > 0 && (
        <Paper withBorder mb="md">
          <Text fw={700} size="sm" p="sm" pb={0}>By Contractor</Text>
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 240 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                  <th style={ctrThStyle('name', 'left')} onClick={() => toggleCtrSort('name')}>Contractor{ctrSortIndicator('name')}</th>
                  <th style={ctrThStyle('totalInvoiced', 'right')} onClick={() => toggleCtrSort('totalInvoiced')}>Total Invoiced{ctrSortIndicator('totalInvoiced')}</th>
                  <th style={ctrThStyle('totalReconciled', 'right')} onClick={() => toggleCtrSort('totalReconciled')}>Reconciled{ctrSortIndicator('totalReconciled')}</th>
                  <th style={ctrThStyle('status', 'center')} onClick={() => toggleCtrSort('status')}>Status{ctrSortIndicator('status')}</th>
                  <th style={ctrThStyle('qualityScored', 'center')} onClick={() => toggleCtrSort('qualityScored')}>Quality Gate{ctrSortIndicator('qualityScored')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedContractorBreakdown.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '6px 12px', fontWeight: 500 }}>{c.name}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtMoney(c.totalInvoiced)}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtMoney(c.totalReconciled)}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                      <Badge size="xs" color={c.status === 'reconciled' ? 'var(--color-success)' : c.status === 'pending' ? '#3b82f6' : '#939598'}>
                        {c.status}
                      </Badge>
                    </td>
                    <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                      <Text size="xs" c={c.qualityScored === c.qualityTotal && c.qualityTotal > 0 ? 'var(--color-success)' : 'dimmed'}>
                        {c.qualityScored}/{c.qualityTotal} scored
                      </Text>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Paper>
      )}

      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Invoice Reconciliation</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingReconciliations} onClick={() => loadReconciliations()}>
            Refresh
          </Button>
          {canEdit && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => {
              setWizardOpen(true);
              setSelectedTxDocId(null);
              // Load transaction detail docs from Evidence Archive
              if (txDocs.length === 0) {
                setTxDocsLoading(true);
                const FDoc = FIELDS.debrisDocuments;
                const docFilters = incidentId ? [{ field: FDoc.incidentId, operator: 'eq' as const, value: incidentId }] : [];
                import('../../api/client').then(({ queryRecords: qrDocs }) =>
                  qrDocs<Record<string, unknown>>(TABLES.debrisDocuments, { limit: 500, filters: docFilters })
                ).then(res => {
                    const relevant = res.records
                      .filter(r => {
                        const docType = String(r[FDoc.documentType] || '');
                        const notes = String(r[FDoc.notes] || '');
                        return docType === 'transaction_detail' || notes.includes('|||TXDATA|||');
                      })
                      .map(r => ({
                        id: String(r.record_id),
                        title: String(r[FDoc.title] || r.record_id),
                        notes: String(r[FDoc.notes] || ''),
                        contractorId: '',
                      }));
                    setTxDocs(relevant);
                  })
                  .catch(() => {})
                  .finally(() => setTxDocsLoading(false));
              }
            }}>
              New Reconciliation
            </Button>
          )}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search contractor, period..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} reconciliation{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                <th style={thStyle('periodStart')} onClick={() => toggleSort('periodStart')}>Date{sortIndicator('periodStart')}</th>
                <th style={thStyle('contractorId')} onClick={() => toggleSort('contractorId')}>Contractor{sortIndicator('contractorId')}</th>
                <th style={{ ...thStyle('periodEnd'), textAlign: 'left' }}>Invoice Period</th>
                <th style={{ ...thStyle('totalInvoiced'), textAlign: 'right' }} onClick={() => toggleSort('totalInvoiced')}>Invoiced{sortIndicator('totalInvoiced')}</th>
                <th style={{ ...thStyle('totalVerified'), textAlign: 'right' }} onClick={() => toggleSort('totalVerified')}>Verified{sortIndicator('totalVerified')}</th>
                <th style={{ ...thStyle('totalVariance'), textAlign: 'right' }} onClick={() => toggleSort('totalVariance')}>Variance{sortIndicator('totalVariance')}</th>
                <th style={{ ...thStyle('variancePercent'), textAlign: 'right' }} onClick={() => toggleSort('variancePercent')}>Var %{sortIndicator('variancePercent')}</th>
                <th style={thStyle('status')} onClick={() => toggleSort('status')}>Status{sortIndicator('status')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'right', position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingReconciliations ? 'Loading...' : 'No reconciliations found'}
                </td></tr>
              )}
              {filtered.map(recon => {
                const isHighVariance = Math.abs(recon.variancePercent) > 2;
                const linkedInvoice = invoices.find(i => String(i.id) === String(recon.invoiceId));
                const payStatus = getPaymentStatus(linkedInvoice);
                const paymentRef = linkedInvoice
                  ? (() => { const m = (linkedInvoice.reviewerNotes || '').match(/paymentRef['":\s]+([^"}\s,]+)/); return m?.[1] ?? ''; })()
                  : '';
                return (
                  <tr key={recon.id}
                    style={{ borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }}
                    onClick={() => setSelectedRecon(recon)}
                  >
                    <td style={{ padding: '8px 16px' }}>{fmtDate(recon.periodStart)}</td>
                    <td style={{ padding: '8px 16px' }}>{contractorName(recon.contractorId)}</td>
                    <td style={{ padding: '8px 16px' }}>
                      <Text size="xs">{fmtDate(recon.periodStart)} — {fmtDate(recon.periodEnd)}</Text>
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                      {fmtMoney(Number(recon.totalInvoiced || 0))}
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                      {fmtMoney(Number(recon.totalVerified || 0))}
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace', color: isHighVariance ? '#D33423' : undefined }}>
                      {Number(recon.totalVariance || 0) > 0 ? '+' : ''}{fmtMoney(Math.abs(Number(recon.totalVariance || 0)))}
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                      <Badge size="sm" color={isHighVariance ? '#C68700' : 'var(--color-success)'}>
                        {fmtPct(recon.variancePercent)}
                      </Badge>
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      <Group gap={4} wrap="wrap">
                        <Badge size="sm" color={STATUS_COLORS[recon.status]}>{STATUS_LABELS[recon.status]}</Badge>
                        <Badge size="xs" style={{
                          background: PAYMENT_COLORS[payStatus].bg,
                          color: PAYMENT_COLORS[payStatus].color,
                          border: `1px solid ${PAYMENT_COLORS[payStatus].border}`,
                        }}>
                          {PAYMENT_COLORS[payStatus].label}
                        </Badge>
                        {paymentRef && (
                          <Text size="xs" ff="monospace" c="dimmed">#{paymentRef}</Text>
                        )}
                      </Group>
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      <Group gap={4} justify="flex-end">
                        {canEdit && <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => setDeleteTarget(recon)}>
                          <IconTrash size={14} />
                        </ActionIcon>}
                      </Group>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* ── New Reconciliation Wizard Modal ──────────────────── */}
      <Modal opened={wizardOpen} onClose={resetWizard}
        title="New Invoice Reconciliation" centered size="xl">
        <Stack>
          <Text size="sm" c="dimmed">
            Pick an invoice to reconcile. Contractor, period, and claimed amounts are pulled from the invoice automatically.
          </Text>

          {/* Step 1: Invoice picker (drives everything else) */}
          <Select
            label="Select Invoice to Reconcile"
            data={invoiceSelectAll}
            value={wizInvoiceId}
            onChange={v => {
              setWizInvoiceId(v);
              setWizCalculated(false);
              setWizLineItems([]);
              setWizExceptions([]);
              if (!v) { setWizContractorId(null); setWizPeriodStart(''); setWizPeriodEnd(''); setWizManualEntries({}); return; }
              const inv = invoices.find(i => String(i.id) === v);
              if (!inv) return;
              setWizContractorId(inv.contractorId);
              setWizPeriodStart(inv.periodStart || '');
              setWizPeriodEnd(inv.periodEnd || '');
              // Pre-populate line items from invoice's stored line items
              try {
                const items: any[] = JSON.parse(inv.lineItems || '[]');
                const entries: Record<string, { qty: number; rate: number; amount: number }> = {};
                items.forEach(item => {
                  const clin = item.lineItemCode || item.clinCode || item.debrisType || item.description || 'unknown';
                  const prev = entries[clin] || { qty: 0, rate: 0, amount: 0 };
                  prev.qty += parseFloat(String(item.quantity || item.invoicedQty || item.cubicYards || 0)) || 0;
                  const r = parseFloat(String(item.rate || item.transactionRate || 0)) || 0;
                  if (r > 0) prev.rate = Math.round(r * 100) / 100;
                  prev.amount += parseFloat(String(item.amount || item.invoicedAmount || item.extendedCost || 0)) || 0;
                  entries[clin] = prev;
                });
                setWizManualEntries(entries);
              } catch { setWizManualEntries({}); }
            }}
            searchable
            clearable
            required
            placeholder="Search by invoice number or contractor..."
          />

          {/* Auto-filled context */}
          {wizInvoiceId && wizContractorId && (
            <Paper p="sm" radius="md" style={{ background: '#f0f4ff', border: '1px solid #c7d2fe' }}>
              <Group gap="md" wrap="wrap">
                <div>
                  <Text size="xs" c="dimmed">Contractor</Text>
                  <Text size="sm" fw={600}>{contractorName(wizContractorId)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Period</Text>
                  <Text size="sm" fw={600}>{fmtDate(wizPeriodStart)} — {fmtDate(wizPeriodEnd)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Invoice Total</Text>
                  <Text size="sm" fw={600}>{fmtMoney(parseFloat(invoices.find(i => String(i.id) === wizInvoiceId)?.totalAmount || '0'))}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Line Items Loaded</Text>
                  <Text size="sm" fw={600}>{Object.keys(wizManualEntries).length} CLINs</Text>
                </div>
              </Group>
            </Paper>
          )}

          <Divider label="Override claimed amounts (optional)" labelPosition="center" />

          {/* File upload to replace line items from invoice */}
          <Paper p="sm" radius="md" withBorder style={{ background: '#fafafa', borderStyle: 'dashed', borderColor: '#cbd5e1' }}>
            <input type="file" accept=".xlsx,.xls,.csv" id="recon-file-input" style={{ display: 'none' }} onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = (ev) => {
                    try {
                      const data = new Uint8Array(ev.target?.result as ArrayBuffer);
                      const wb = XLSX.read(data, { type: 'array' });
                      const ws = wb.Sheets[wb.SheetNames[0]];
                      const json = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { raw: false, defval: '' });

                      if (json.length === 0) {
                        notifications.show({ message: 'File is empty', color: '#C68700' });
                        return;
                      }

                      const groups: Record<string, { qty: number; rate: number; amount: number; count: number; desc: string }> = {};
                      json.forEach(row => {
                        const clin = row['Service'] || row['Rate Code'] || row['CLIN'] || row['CLIN Code'] || row['clinCode'] || '';
                        if (!clin) return;
                        const qty = parseFloat(row['Quantity'] || row['CYD'] || row['Qty'] || '0') || 0;
                        const rate = parseFloat(row['Rate'] || row['Transaction Rate'] || row['Unit Rate'] || '0') || 0;
                        const totalRaw = row[' Total '] || row['Total'] || row['Extended Cost'] || row['Amount'] || '0';
                        const amount = parseFloat(String(totalRaw).replace(/[$,\s]/g, '')) || 0;
                        const desc = row['Description'] || row['Rate Description'] || '';

                        if (!groups[clin]) groups[clin] = { qty: 0, rate: 0, amount: 0, count: 0, desc: '' };
                        groups[clin].qty += qty;
                        groups[clin].amount += amount;
                        if (rate > 0) groups[clin].rate = rate;
                        groups[clin].count++;
                        if (desc && !groups[clin].desc) groups[clin].desc = desc;
                      });

                      const entries: Record<string, { qty: number; rate: number; amount: number }> = {};
                      Object.entries(groups).forEach(([clin, g]) => {
                        entries[clin] = {
                          qty: Math.round(g.qty * 100) / 100,
                          rate: Math.round(g.rate * 100) / 100,
                          amount: Math.round(g.amount * 100) / 100,
                        };
                      });
                      setWizManualEntries(entries);
                      setSelectedTxDocId(null);

                      const totalAmt = Object.values(entries).reduce((s, e) => s + e.amount, 0);
                      const totalQty = Object.values(entries).reduce((s, e) => s + e.qty, 0);
                      notifications.show({
                        message: `Loaded ${Object.keys(entries).length} CLIN codes from ${json.length} rows — ${totalQty.toLocaleString()} units, ${fmtMoney(totalAmt)}`,
                        color: 'var(--color-success)',
                      });
                    } catch (err) {
                      console.error('[Reconciliation] parse error', err);
                      notifications.show({ message: 'Failed to parse file', color: 'var(--color-error)' });
                    }
                  };
                  reader.readAsArrayBuffer(file);
                  e.target.value = '';
                }} />
            <Group gap="sm" align="center">
              <IconUpload size={18} color="#94a3b8" />
              <div style={{ flex: 1 }}>
                <Text size="sm" fw={500} c="dimmed">Upload Transaction Detail to Override</Text>
                <Text size="xs" c="dimmed">XLSX/CSV — replaces the line items loaded from the invoice above</Text>
              </div>
              <Button variant="subtle" size="xs" color="dimmed" leftSection={<IconUpload size={14} />}
                onClick={() => document.getElementById('recon-file-input')?.click()}>
                Choose File
              </Button>
            </Group>
          </Paper>

          <Text size="xs" c="dimmed" style={{ textAlign: 'center' }}>— or enter manually —</Text>
          <Paper p="sm" radius="md" withBorder style={{ background: '#f8fafc' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                    <th style={{ padding: '6px 10px', textAlign: 'left', minWidth: 180 }}>CLIN Code</th>
                    <th style={{ padding: '6px 10px', textAlign: 'right', minWidth: 100 }}>Quantity</th>
                    <th style={{ padding: '6px 10px', textAlign: 'right', minWidth: 90 }}>Rate ($/unit)</th>
                    <th style={{ padding: '6px 10px', textAlign: 'right', minWidth: 120 }}>Total ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // If entries are loaded from an invoice, show only those rows.
                    // If no entries yet, show the full rate-schedule CLIN list for manual entry.
                    const hasEntries = Object.keys(wizManualEntries).length > 0;
                    const rows = hasEntries
                      ? Object.entries(wizManualEntries).map(([key, entry]) => {
                          // Try to find a friendly label from clinOptions
                          const opt = clinOptions.find(o => o.value === key);
                          const label = opt?.label || key;
                          return { key, label, entry, rate: opt?.rate };
                        })
                      : clinOptions.map(opt => ({
                          key: opt.value,
                          label: opt.label,
                          entry: wizManualEntries[opt.value] || { qty: 0, rate: opt.rate || 0, amount: 0 },
                          rate: opt.rate,
                        }));

                    const cellInput: React.CSSProperties = {
                      textAlign: 'right', fontFamily: 'monospace', width: '100%',
                      border: '1px solid #e2e8f0', borderRadius: 4,
                      padding: '3px 6px', fontSize: '0.8rem', background: '#fff',
                    };
                    return rows.map(({ key, label, entry }) => (
                      <tr key={key} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '4px 10px' }}>
                          <Text size="xs" fw={500}>{label}</Text>
                        </td>
                        <td style={{ padding: '4px 10px' }}>
                          <input
                            type="text"
                            style={cellInput}
                            value={entry.qty > 0 ? entry.qty.toLocaleString('en-US', { maximumFractionDigits: 2 }) : ''}
                            placeholder="0"
                            onChange={e => {
                              const qty = parseFloat(e.target.value.replace(/,/g, '')) || 0;
                              const rate = entry.rate || 0;
                              setWizManualEntries(prev => ({
                                ...prev,
                                [key]: { qty, rate, amount: Math.round(qty * rate * 100) / 100 },
                              }));
                            }}
                          />
                        </td>
                        <td style={{ padding: '4px 10px' }}>
                          <input
                            type="text"
                            style={cellInput}
                            value={entry.rate > 0 ? '$' + entry.rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                            placeholder="$0.00"
                            onChange={e => {
                              const rate = parseFloat(e.target.value.replace(/[$,]/g, '')) || 0;
                              const qty = entry.qty || 0;
                              setWizManualEntries(prev => ({
                                ...prev,
                                [key]: { qty, rate, amount: Math.round(qty * rate * 100) / 100 },
                              }));
                            }}
                          />
                        </td>
                        <td style={{ padding: '4px 10px' }}>
                          <input
                            type="text"
                            style={cellInput}
                            value={entry.amount > 0 ? fmtMoney(entry.amount) : ''}
                            placeholder="$0.00"
                            onChange={e => {
                              const amount = parseFloat(e.target.value.replace(/[$,]/g, '')) || 0;
                              setWizManualEntries(prev => ({
                                ...prev,
                                [key]: { ...entry, amount },
                              }));
                            }}
                          />
                        </td>
                      </tr>
                    ));
                  })()}
                  <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 700 }}>
                    <td style={{ padding: '6px 10px' }}>Total</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>
                      {Object.values(wizManualEntries).reduce((s, e) => s + (e.qty || 0), 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '6px 10px' }}></td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>
                      {fmtMoney(Object.values(wizManualEntries).reduce((s, e) => s + (e.amount || 0), 0))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Paper>

          {/* Service invoice toggle */}
          <Paper p="sm" radius="md" style={{
            background: wizServiceInvoice ? '#f0fdf4' : '#f8fafc',
            border: `1px solid ${wizServiceInvoice ? '#86efac' : '#e2e8f0'}`,
            cursor: 'pointer',
          }} onClick={() => { setWizServiceInvoice(v => !v); setWizCalculated(false); }}>
            <Group gap="sm" align="flex-start">
              <div style={{
                width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1,
                border: `2px solid ${wizServiceInvoice ? 'var(--color-success)' : '#94a3b8'}`,
                background: wizServiceInvoice ? 'var(--color-success)' : '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {wizServiceInvoice && <svg width="10" height="10" viewBox="0 0 10 10"><polyline points="1.5,5 4,7.5 8.5,2" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              <div>
                <Text size="sm" fw={600} c={wizServiceInvoice ? 'var(--color-success)' : '#374151'}>
                  Service invoice — no tickets expected
                </Text>
                <Text size="xs" c="dimmed">
                  For electrical, fencing, monitoring, equipment rental, and other non-debris services. Invoice amounts are accepted as verified with zero variance.
                </Text>
              </div>
            </Group>
          </Paper>

          {/* Run reconciliation button */}
          <Button leftSection={<IconScale size={16} />} color="var(--data-viz-650)"
            onClick={runReconciliation} variant="light" loading={reconLoading}
            disabled={reconLoading || !wizInvoiceId}>
            {reconLoading ? 'Loading tickets...' : wizServiceInvoice ? 'Accept Service Invoice' : 'Run Reconciliation'}
          </Button>

          {/* Step 3: Results */}
          {wizCalculated && (
            <>
              <Divider />

              {/* Summary */}
              <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="xs">
                <Paper p="sm" radius="md" withBorder style={{ textAlign: 'center' }}>
                  <Text size="xs" c="dimmed">Invoiced</Text>
                  <Text fw={700} c="#3b82f6">{fmtMoney(wizTotalInvoiced)}</Text>
                </Paper>
                <Paper p="sm" radius="md" withBorder style={{ textAlign: 'center' }}>
                  <Text size="xs" c="dimmed">Verified</Text>
                  <Text fw={700} c="var(--color-success)">{fmtMoney(wizTotalVerified)}</Text>
                </Paper>
                <Paper p="sm" radius="md" withBorder style={{ textAlign: 'center' }}>
                  <Text size="xs" c="dimmed">Variance</Text>
                  <Text fw={700} c={Math.abs(wizTotalInvoiced - wizTotalVerified) > 0.01 ? '#C68700' : 'var(--color-success)'}>
                    {(wizTotalInvoiced - wizTotalVerified) > 0 ? '+' : ''}{fmtMoney(Math.abs(wizTotalInvoiced - wizTotalVerified))}
                  </Text>
                </Paper>
                <Paper p="sm" radius="md" withBorder style={{ textAlign: 'center' }}>
                  <Text size="xs" c="dimmed">Recommended</Text>
                  <Text fw={700} c="var(--color-success)">{fmtMoney(wizTotalVerified)}</Text>
                </Paper>
                <Paper p="sm" radius="md" withBorder style={{ textAlign: 'center' }}>
                  <Text size="xs" c="dimmed">Exceptions</Text>
                  <Text fw={700} c={wizExceptions.length > 0 ? 'var(--color-error)' : 'var(--color-success)'}>
                    {wizExceptions.length}
                  </Text>
                </Paper>
              </SimpleGrid>

              {/* Line items result table */}
              {wizLineItems.length > 0 && (
                <Paper withBorder>
                  <div style={{ overflowX: 'auto', maxHeight: 300 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                          <th style={{ padding: '6px 12px', textAlign: 'left', position: 'sticky', top: 0, background: '#f8fafc' }}>CLIN</th>
                          <th style={{ padding: '6px 12px', textAlign: 'right', position: 'sticky', top: 0, background: '#f8fafc' }}>Invoiced</th>
                          <th style={{ padding: '6px 12px', textAlign: 'right', position: 'sticky', top: 0, background: '#f8fafc' }}>Verified</th>
                          <th style={{ padding: '6px 12px', textAlign: 'right', position: 'sticky', top: 0, background: '#f8fafc' }}>Variance</th>
                          <th style={{ padding: '6px 12px', textAlign: 'right', position: 'sticky', top: 0, background: '#f8fafc' }}>Var %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wizLineItems.map((li, idx) => {
                          const isHigh = Math.abs(li.variancePercent) > 2;
                          return (
                            <tr key={idx} style={{
                              borderBottom: '1px solid #e2e8f0',
                              backgroundColor: isHigh ? (Math.abs(li.variancePercent) > 10 ? '#fef2f2' : '#fffbeb') : undefined,
                            }}>
                              <td style={{ padding: '6px 12px' }}>{clinLabel(li.clinCode || '') !== '-' ? clinLabel(li.clinCode || '') : li.description}</td>
                              <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{li.invoicedQty.toLocaleString()}</td>
                              <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{li.verifiedQty.toLocaleString()}</td>
                              <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace', color: isHigh ? '#D33423' : undefined }}>
                                {li.variance > 0 ? '+' : ''}{li.variance.toLocaleString()}
                              </td>
                              <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                                <Badge size="xs" color={isHigh ? '#C68700' : 'var(--color-success)'}>{fmtPct(li.variancePercent)}</Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Paper>
              )}

              {/* Exceptions */}
              {wizExceptions.length > 0 && (
                <Alert color="#C68700">
                  <Text size="sm" fw={600} mb={4}>{wizExceptions.length} Exception{wizExceptions.length !== 1 ? 's' : ''} Found</Text>
                  {wizExceptions.map((ex, idx) => (
                    <Text key={idx} size="xs">
                      <Badge size="xs" color={ex.reason === 'phantom' ? 'var(--color-error)' : '#C68700'} mr={4}>{ex.reason}</Badge>
                      {ex.ticketNumber}: {ex.contractorValue || ''} {ex.monitorValue ? `vs. ${ex.monitorValue}` : ''} {ex.notes ? `(${ex.notes})` : ''}
                    </Text>
                  ))}
                </Alert>
              )}

              <Textarea label="Reviewer Notes" value={wizNotes}
                onChange={e => setWizNotes(e.target.value)} autosize minRows={2} />
            </>
          )}

          <Divider />
          <Group justify="flex-end">
            <Button variant="default" onClick={resetWizard}>Cancel</Button>
            <Button loading={saving} onClick={handleSaveReconciliation}
              disabled={!wizCalculated || !wizContractorId}>
              Save Reconciliation
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Source ticket drill-down */}
      <Modal
        opened={!!drillDownLineItem}
        onClose={() => setDrillDownLineItem(null)}
        title={drillDownLineItem ? `Source Tickets — CLIN ${drillDownLineItem.clinCode}` : ''}
        size="lg"
        centered
      >
        {drillDownLineItem && (
          <Stack gap={12}>
            <Group gap={24}>
              <div><Text size="xs" c="dimmed">Verified Qty</Text><Text fw={600}>{drillDownLineItem.verifiedQty.toLocaleString()}</Text></div>
              <div><Text size="xs" c="dimmed">Verified $</Text><Text fw={600}>{fmtMoney(drillDownLineItem.verifiedAmount)}</Text></div>
              <div><Text size="xs" c="dimmed">Tickets</Text><Text fw={600}>{(drillDownLineItem.ticketNumbers ?? []).length}</Text></div>
            </Group>
            <div style={{ overflowY: 'auto', maxHeight: 400 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #cbd5e1', fontWeight: 600, color: '#64748b' }}>#</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #cbd5e1', fontWeight: 600, color: '#64748b' }}>Ticket Number</th>
                  </tr>
                </thead>
                <tbody>
                  {(drillDownLineItem.ticketNumbers ?? []).map((tn, i) => {
                    const tid = (drillDownLineItem as any).ticketIds?.[i] ?? '';
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                        <td style={{ padding: '6px 12px', color: '#94a3b8' }}>{i + 1}</td>
                        <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontWeight: 500 }}>
                          {tid ? (
                            <button
                              onClick={() => { setDrillDownLineItem(null); navigate(`/debris/tickets/${tid}`); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4B3FFF', fontFamily: 'monospace', fontWeight: 600, fontSize: 13, padding: 0, textDecoration: 'underline' }}
                            >
                              {tn}
                            </button>
                          ) : (
                            <span>{tn}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Text size="xs" c="dimmed">
              Ticket numbers captured at reconciliation time. Navigate to Load Tickets to see full ticket details.
            </Text>
          </Stack>
        )}
      </Modal>

      {/* Approve amount-change confirm */}
      <Modal opened={!!approveConfirm} onClose={() => setApproveConfirm(null)} title="Confirm Approval — Amount Change" centered size="sm">
        {approveConfirm && (
          <Stack gap={12}>
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
              This will change the existing invoice total by more than 5%.
            </Alert>
            <SimpleGrid cols={2} spacing="xs">
              <Paper p="xs" radius="sm" withBorder>
                <Text size="xs" c="dimmed">Current invoice total</Text>
                <Text size="sm" fw={600}>{fmtMoney(approveConfirm.existingTotal)}</Text>
              </Paper>
              <Paper p="xs" radius="sm" withBorder>
                <Text size="xs" c="dimmed">New approved amount</Text>
                <Text size="sm" fw={600} c={approveConfirm.newTotal > approveConfirm.existingTotal ? 'var(--color-success)' : 'var(--color-error)'}>
                  {fmtMoney(approveConfirm.newTotal)}
                </Text>
              </Paper>
            </SimpleGrid>
            <Group justify="flex-end">
              <Button variant="default" size="xs" onClick={() => setApproveConfirm(null)}>Cancel</Button>
              <Button color="var(--color-success)" size="xs" onClick={() => {
                const recon = approveConfirm.recon;
                setApproveConfirm(null);
                void handleApprove(recon);
              }}>Approve Anyway</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Re-open confirm */}
      <Modal opened={!!reopenTarget} onClose={() => setReopenTarget(null)} title="Re-open Reconciliation" centered size="sm">
        <Stack gap={12}>
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
            This will clear the approval and move the reconciliation back to <strong>In Review</strong>.
            The associated invoice status will not be automatically changed.
          </Alert>
          <Text size="sm">
            Re-open reconciliation for <strong>{reopenTarget ? contractorName(reopenTarget.contractorId) : ''}</strong>?
          </Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setReopenTarget(null)}>Cancel</Button>
            <Button color="blue" size="xs" onClick={() => reopenTarget && handleReopen(reopenTarget)}>Re-open</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Reconciliation" centered size="sm">
        <Stack>
          <Text size="sm">Delete this reconciliation for <strong>{deleteTarget ? contractorName(deleteTarget.contractorId) : ''}</strong>?</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
